import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "./ui/select";

const lookingOptions = [
  { value: "diagnose", title: "Diagnose", desc: "I need help understanding the problem" },
  { value: "consult", title: "Consult", desc: "I need strategic marketing guidance" },
  { value: "growth-partner", title: "Growth Partner", desc: "I want GroundWork to help execute and grow" },
  { value: "let-us-decide", title: "Let us decide", desc: "I know something needs to change, but I'm not sure where to start" },
];

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", lookingFor: "", note: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email so we can get back to you.");
      return;
    }
    if (!form.lookingFor) {
      toast.error("Please choose what you're looking for.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg =
          typeof data.detail === "string"
            ? data.detail
            : "Please check your details and try again.";
        throw new Error(msg);
      }
      toast.success("Thanks, we'll be in touch shortly.", {
        description: `Pitch sent for ${form.name}.`,
      });
      setForm({ name: "", email: "", company: "", lookingFor: "", note: "" });
    } catch (err) {
      toast.error(err.message || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#E26D5C] text-[#F7F5F0] py-24 md:py-40 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6 reveal">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#F7F5F0]/80">
              (07) · Let's talk
            </span>
            <h2 className="mt-6 font-display text-5xl sm:text-6xl lg:text-8xl font-light tracking-tight leading-[1]">
              Build with <span className="italic">us.</span>
            </h2>
            <p className="mt-8 max-w-xl text-base md:text-lg font-light text-[#F7F5F0]/90">
              Bring us the problem you're trying to solve. We'll understand the
              business, challenge the obvious answer where necessary, and work
              out what should happen next.
            </p>

            <div className="mt-12 space-y-6 text-[#F7F5F0]/90">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] opacity-70">
                  Say hello
                </div>
                <a
                  href="mailto:hello@the-groundwork.com"
                  data-testid="contact-email"
                  className="font-display text-3xl md:text-4xl link-underline"
                >
                  hello@the-groundwork.com
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="md:col-span-6 min-w-0 reveal bg-[#F7F5F0] text-[#1C1C19] rounded-3xl p-8 md:p-12 space-y-6"
            data-testid="contact-form"
          >
            <h3 className="font-display text-3xl font-light tracking-tight">
              Tell us about your brand.
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#5C5C58]">
                  Your name
                </label>
                <input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-transparent border-b border-[#D8D4C7] focus:border-[#E26D5C] outline-none py-2"
                  placeholder="Your name"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#5C5C58]">
                  Email
                </label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-transparent border-b border-[#D8D4C7] focus:border-[#E26D5C] outline-none py-2"
                  placeholder="you@brand.co"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#5C5C58]">
                Company
              </label>
              <input
                data-testid="contact-input-company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="bg-transparent border-b border-[#D8D4C7] focus:border-[#E26D5C] outline-none py-2"
                placeholder="Your company"
              />
            </div>
            <div className="flex flex-col gap-2 min-w-0">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#5C5C58]">
                What are you looking for?
              </label>
              <Select
                value={form.lookingFor}
                onValueChange={(v) => setForm({ ...form, lookingFor: v })}
              >
                <SelectTrigger
                  data-testid="contact-input-looking-for"
                  className="w-full min-w-0 rounded-none border-0 border-b border-[#D8D4C7] bg-transparent px-0 py-2 h-auto text-base text-[#1C1C19] shadow-none focus:ring-0 focus:border-[#E26D5C]"
                >
                  {form.lookingFor ? (
                    <span className="truncate">
                      {lookingOptions.find((o) => o.value === form.lookingFor)?.title}
                    </span>
                  ) : (
                    <span className="text-[#5C5C58]/70">Select one</span>
                  )}
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-3rem)] bg-[#F7F5F0] border-[#D8D4C7]">
                  {lookingOptions.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`looking-for-${o.value}`}
                      className="py-2.5 focus:bg-[#E26D5C]/10"
                    >
                      <span className="flex flex-col gap-0.5">
                        <span className="font-display text-lg text-[#1C1C19] leading-tight">
                          {o.title}
                        </span>
                        <span className="text-xs text-[#5C5C58] whitespace-normal">
                          {o.desc}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#5C5C58]">
                What are you building?
              </label>
              <textarea
                data-testid="contact-input-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={4}
                className="bg-transparent border-b border-[#D8D4C7] focus:border-[#E26D5C] outline-none py-2 resize-none"
                placeholder="One paragraph on the brand, the stage you're at, and what you'd want from a partner like us."
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={submitting}
              className="btn-magnetic w-full inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#1C1C19] text-[#F7F5F0] text-sm font-semibold uppercase tracking-widest hover:bg-[#E26D5C] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Build with us"}
              <ArrowUpRight size={16} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
